import {AnalysisListType} from '@hconnect/common'
import {trackEvent} from '@hconnect/common/logging/Analytics'
import {useUserContext} from '@hconnect/login'
import {Loader} from '@hconnect/uikit'
import {useUpmPlant, useUpmPlantsContext} from '@hconnect/upm'
import {usePermission} from '@hproduce/permissions'
import {Box, Grid} from '@mui/material'
import {AssetHealth, KilnMonitoringSchematicWidget} from '@widgets/kms'
import {RcfaOverview} from '@widgets/rcfa-overview'
import {useCallback} from 'react'

type MaintainDashboardProps = {
  plantId: string
  onNavigateToRcfaNew: () => void
  onNavigateToRcfaList: (listType: AnalysisListType) => void
  onNavigateToKiln?: (kilnId: string) => void
  onSvgClick?: (kilnId: string, localNames: string[]) => void
  onMetricClick?: (kilnId: string, localNames: string[]) => void
  onCrankClick?: (kilnId: string) => void
  onNavigateToSignals?: () => void
  onNavigateToSignal?: (localName: string) => void
}

export const MaintainDashboardContent = ({
  plantId,
  onNavigateToRcfaNew,
  onNavigateToRcfaList,
  onNavigateToKiln,
  onSvgClick,
  onMetricClick,
  onCrankClick,
  onNavigateToSignals,
  onNavigateToSignal
}: MaintainDashboardProps) => {
  const {loggedUser} = useUserContext()
  const {upmId, country} = useUpmPlant({plantId, shouldRaiseError: true})

  const plantContext = {plantId, countryId: country}

  const hasRcfaAccess = usePermission({
    plantContext,
    permissionTypes: ['CONTRIBUTE_TO_ROOT_CAUSE_FAILURE_ANALYSES']
  })

  const hasPmAccess = usePermission({
    plantContext,
    permissionTypes: ['VIEW_KILN_MONITORING_DATA']
  })

  const handleTrackEvent = useCallback(
    (name: string) =>
      loggedUser &&
      trackEvent(name, {
        userId: loggedUser.id,
        country: loggedUser.country,
        product: 'Maintain',
        subProduct: 'KMS'
      }),
    [loggedUser]
  )

  return (
    <Box p={{xs: 2, md: 4}}>
      <Grid container direction={'row'} spacing={{xs: 2, md: 4}}>
        {hasPmAccess && (
          <>
            <KilnMonitoringSchematicWidget
              title="KMS"
              plantUpmId={upmId}
              onNavigateToKiln={onNavigateToKiln}
              onSvgClick={onSvgClick}
              onMetricClick={onMetricClick}
              onCrankClick={onCrankClick}
              onTrackEvent={(name) => handleTrackEvent(name)}
            />
            <AssetHealth
              plantUpmId={upmId}
              onNavigateToSignals={onNavigateToSignals}
              onNavigateToSignal={onNavigateToSignal}
            />
          </>
        )}
        {hasRcfaAccess && (
          <RcfaOverview
            plantUpmId={upmId}
            onNavigateToNew={onNavigateToRcfaNew}
            onNavigateToList={onNavigateToRcfaList}
          />
        )}
      </Grid>
    </Box>
  )
}

export const MaintainDashboard = (props: MaintainDashboardProps) => {
  // we need to make sure the plants are loaded
  const upmPlants = useUpmPlantsContext({isInitialLoad: true})

  if (!upmPlants) {
    return <Loader />
  }

  return <MaintainDashboardContent {...props} />
}
